import { animate, stagger } from 'animejs'
import { ArrowDown, Droplets, Flame, Gauge, Snowflake } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import ModelLoader from './components/ModelLoader'
import ProductCanvas from './components/ProductCanvas'
import SiteHeader from './components/SiteHeader'
import StoryProgress from './components/StoryProgress'
import StorySection, { Metric } from './components/StorySection'
import ViewerControls from './components/ViewerControls'
import { chapters, explodedLabels } from './data/chapters'
import { useStoryScroll } from './hooks/useStoryScroll'

function useReducedMotion() {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(query.matches)
    update()
    if (query.addEventListener) {
      query.addEventListener('change', update)
      return () => query.removeEventListener('change', update)
    }

    query.addListener(update)
    return () => query.removeListener(update)
  }, [])

  return reduced
}

function Hero() {
  const root = useRef(null)

  useEffect(() => {
    const targets = root.current?.querySelectorAll('[data-hero-reveal]')
    if (!targets?.length) return undefined
    const animation = animate(targets, {
      opacity: [0, 1],
      translateY: [26, 0],
      duration: 920,
      delay: stagger(95, { start: 140 }),
      ease: 'outExpo',
    })
    return () => animation.cancel()
  }, [])

  return (
    <section ref={root} id="hero" data-scene data-theme="dark" className="hero">
      <div className="hero__content">
        <p className="hero__eyebrow" data-hero-reveal>
          STOVETOP ESPRESSO SYSTEM / 01
        </p>
        <h1 data-hero-reveal>
          <span>MoLiKa</span>
          <strong>9bar</strong>
        </h1>
        <p className="hero__lede" data-hero-reveal>
          炉火制造压力，金属控制温度，
          <br />
          一杯浓缩向上完成。
        </p>
        <div className="hero__metrics" data-hero-reveal>
          <Metric value="9 BAR" label="目标萃取压力" />
          <Metric value="02" label="连续换热阶段" />
          <Metric value="00 W" label="外接电力" />
        </div>
      </div>
      <a className="hero__next" href="#system" aria-label="进入系统总览" data-hero-reveal>
        <ArrowDown aria-hidden="true" />
      </a>
      <div className="hero__index" aria-hidden="true">
        M9 / 001
      </div>
    </section>
  )
}

function ProcessStrip() {
  const items = [
    [Flame, '热输入', '密闭锅炉'],
    [Gauge, '压力触发', '机械阀门'],
    [Snowflake, '双级换热', '液体与空气'],
    [Droplets, '向上萃取', '承接浓缩'],
  ]

  return (
    <div className="process-strip">
      {items.map(([Icon, title, detail], index) => (
        <div key={title} className="process-strip__item">
          <span>{String(index + 1).padStart(2, '0')}</span>
          <Icon aria-hidden="true" />
          <strong>{title}</strong>
          <small>{detail}</small>
        </div>
      ))}
    </div>
  )
}

function CutawaySection() {
  const labels = [
    ['上层', '浓缩承接杯'],
    ['萃取', '粉篮与萃取腔'],
    ['调温', '鳍片与盘管换热器'],
    ['触发', '弹簧加载阀'],
    ['底层', '高压锅炉'],
  ]

  return (
    <section id="cutaway" data-scene data-theme="paper" className="cutaway-section">
      <div className="cutaway-section__heading">
        <p className="eyebrow">
          <span>08</span>
          INTERNAL ARCHITECTURE
        </p>
        <h2>看见压力<br />如何向上。</h2>
      </div>
      <div className="cutaway-section__figure">
        <img src="/images/product-cutaway.webp" alt="MoLiKa 9bar 内部结构剖面" />
      </div>
      <ol className="cutaway-section__labels">
        {labels.map(([name, detail]) => (
          <li key={name}>
            <span>{name}</span>
            <strong>{detail}</strong>
          </li>
        ))}
      </ol>
    </section>
  )
}

function ExploreSection() {
  return (
    <section id="explore" data-scene data-theme="dark" className="explore-section">
      <p className="eyebrow">
        <span>09</span>
        OBJECT VIEWER
      </p>
      <h2>一台机器。<br />每一层都有答案。</h2>
      <div className="explore-section__axis" aria-hidden="true">
        <span>X</span>
        <span>Y</span>
        <span>Z</span>
      </div>
    </section>
  )
}

function GallerySection() {
  return (
    <section id="gallery" data-scene data-theme="paper" className="gallery-section">
      <header className="gallery-section__header">
        <p className="eyebrow">
          <span>10</span>
          INDUSTRIAL DESIGN
        </p>
        <h2>外观克制，<br />内部精密。</h2>
        <p>不锈钢机身、双木质握柄与垂直热力结构，围绕炉火上的稳定工作而设计。</p>
      </header>
      <div className="gallery-grid">
        <figure className="gallery-grid__exterior">
          <img src="/images/product-exterior.webp" alt="MoLiKa 9bar 外观渲染" />
          <figcaption>FORM / 整机外观</figcaption>
        </figure>
        <figure className="gallery-grid__transparent">
          <img src="/images/product-transparent.webp" alt="MoLiKa 9bar 透明结构渲染" />
          <figcaption>FUNCTION / 内部路径</figcaption>
        </figure>
      </div>
      <dl className="spec-band">
        <div>
          <dt>动力来源</dt>
          <dd>炉火热能</dd>
        </div>
        <div>
          <dt>压力控制</dt>
          <dd>机械弹簧阀</dd>
        </div>
        <div>
          <dt>温度控制</dt>
          <dd>液体 + 空气换热</dd>
        </div>
        <div>
          <dt>CAD 装配</dt>
          <dd>10 组结构根</dd>
        </div>
      </dl>
      <footer className="site-footer">
        <div>
          <span>MoLiKa</span>
          <strong>9bar</strong>
        </div>
        <p>炉火之上，自成九巴。</p>
        <a href="#hero">返回顶部</a>
      </footer>
    </section>
  )
}

export default function App() {
  const story = useStoryScroll()
  const reducedMotion = useReducedMotion()
  const [mode, setMode] = useState('assembled')
  const activeChapter = chapters[Math.min(story.index, chapters.length - 1)]
  const interactive = activeChapter.id === 'explore'

  useEffect(() => {
    document.documentElement.dataset.theme = activeChapter.theme
  }, [activeChapter.theme])

  const canvasStory = useMemo(
    () => ({ ...story, index: Math.min(story.index, chapters.length - 1) }),
    [story],
  )

  return (
    <>
      <div className={`page-surface page-surface--${activeChapter.theme}`} aria-hidden="true" />
      <div className="viewport-grid" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>
      <SiteHeader theme={activeChapter.theme} />
      <ProductCanvas
        story={canvasStory}
        mode={mode}
        interactive={interactive}
        reducedMotion={reducedMotion}
      />
      <ModelLoader />
      <StoryProgress chapters={chapters} activeIndex={story.index} total={story.total} />
      <ViewerControls value={mode} onChange={setMode} visible={interactive} />

      <main>
        <Hero />
        <StorySection
          id="system"
          index={1}
          eyebrow="THERMAL ARCHITECTURE"
          title={<>一条向上的<br />热力路径。</>}
          align="right"
          theme="light"
        >
          <p>
            从底部高压锅炉到顶部承接杯，十组装配件围绕同一条垂直轴线协同。加压、触发、换热与萃取，按顺序发生。
          </p>
          <ProcessStrip />
        </StorySection>

        <StorySection
          id="pressure"
          index={2}
          eyebrow="HIGH-PRESSURE BOILER"
          title={<>炉火先<br />制造压力。</>}
          align="left"
          theme="dark"
        >
          <p>
            密闭锅炉把持续热输入转化为温度与压力。厚壁金属腔体稳定承载，让系统从炉火开始建立九巴目标工况。
          </p>
          <div className="inline-metrics">
            <Metric value="9 BAR" label="设计目标" />
            <Metric value="HEAT" label="唯一动力" />
          </div>
        </StorySection>

        <StorySection
          id="thermal"
          index={3}
          eyebrow="DUAL HEAT EXCHANGE"
          title={<>把压力留下，<br />把温度降下来。</>}
          align="right"
          theme="light"
        >
          <p>
            高压水首先进入盘管，由外部水浴带走热量；随后流经鳍片结构，与空气完成第二段换热，抵达适合萃取的温区。
          </p>
          <div className="thermal-legend">
            <span><i className="thermal-legend__hot" />高温高压</span>
            <span><i className="thermal-legend__cool" />调温高压</span>
          </div>
        </StorySection>

        <StorySection
          id="valve"
          index={4}
          eyebrow="SPRING-LOADED VALVE"
          title={<>九巴，<br />由机械决定。</>}
          align="left"
          theme="dark"
        >
          <p>
            弹簧预载定义开启阈值。压力达到设计值，阀芯抬升、通路打开；无需传感器，也无需电子控制器。
          </p>
          <div className="valve-signature">
            <Gauge aria-hidden="true" />
            <span>PRESSURE / RELEASE / FLOW</span>
          </div>
        </StorySection>

        <StorySection
          id="brew"
          index={5}
          eyebrow="UPWARD EXTRACTION"
          title={<>最后一段路，<br />穿过咖啡。</>}
          align="right"
          theme="light"
        >
          <p>
            完成调温的高压水向上进入粉篮，穿过咖啡粉层。萃取液继续上升，在顶部承接杯中完成汇集。
          </p>
          <div className="brew-sequence" aria-label="萃取流程">
            <span>WATER</span><i />
            <span>COFFEE</span><i />
            <span>ESPRESSO</span>
          </div>
        </StorySection>

        <StorySection
          id="exploded"
          index={6}
          eyebrow="EXPLODED ASSEMBLY"
          title={<>每一层，<br />都为压力让路。</>}
          align="left"
          theme="dark"
          className="exploded-section"
        >
          <ol className="exploded-list">
            {explodedLabels.map(([number, label]) => (
              <li key={number}>
                <span>{number}</span>
                <strong>{label}</strong>
              </li>
            ))}
          </ol>
        </StorySection>

        <CutawaySection />
        <ExploreSection />
        <GallerySection />
      </main>
    </>
  )
}
